import { useForm } from 'react-hook-form'

import { Modal } from 'antd'
import { DataContext } from 'contexts/DataContextContainer'
import { useContext, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector'
import axiosClient from 'services/axios-client'
import styled from 'styled-components'

const ModalInnerContainer = styled.div`
  padding: 40px;

  .shipaddr-instructions {
    margin-left: -20px;
  }

  .shipaddr-field {
    margin-bottom: 15px;
  }
`

const ShippingAddressModal = ({
  bookID,
  purchaserID,
  panelRef,
  show,
  onHide,
  onUpdate,
  disabled,
}) => {
  const { loggedInUser } = useContext(DataContext)
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm()
  const [submitErr, setSubmitErr] = useState('')
  const [country, setCountry] = useState('')
  const [state, setState] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    register('country', { required: true })
    register('state', { required: true })
  }, [register])

  useEffect(() => {
    if (!purchaserID) return

    setLoading(true)

    const fetchAddr = async () => {
      axiosClient
        .get(`/users/${purchaserID}/address`) // Using purchaser addr as shipping addr
        .then(({ data }) => {
          setValue('name', data.name)
          setValue('address1', data.address1)
          setValue('address2', data.address2)
          setValue('city', data.city)
          setCountry(data.country)
          setValue('country', data.country)
          setState(data.state)
          setValue('state', data.state)
          setValue('zip', data.zip)
          setValue('phone', data.phone)

          setLoading(false)
        })
        .catch((err) => {
          console.error(err)
        })
    }

    fetchAddr().catch((err) => console.error(err))
  }, [show, loggedInUser, setLoading, setValue, purchaserID])

  const onSubmit = (data) => {
    setSubmitErr('')

    try {
      setLoading(true)
      axiosClient
        .put(`/users/${purchaserID}/address`, {
          ...data,
          bookID: bookID,
        })
        .then(() => {
          axiosClient
            .put(`/book/${bookID}`, {
              address_confirmed: true,
            })
            .then(({ ok }) => {
              if (ok) {
                onHide()
                setLoading(false)
                onUpdate()
              }
            })
        })
    } catch (err) {
      setSubmitErr(err)

      setLoading(false)
    }
  }

  return (
    <Modal
      title="Shipping Address"
      open={show}
      onCancel={onHide}
      destroyOnClose={true}
      closable={true}
      footer={false}
      maskClosable={false}
      wrapClassName="modal-wrapper"
      getContainer={() => panelRef.current}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalInnerContainer className="modal-inner">
          <p className="shipaddr-instructions">
            Please add/ update the address you want your books shipped to.
          </p>
          <p className="error">{submitErr}</p>
          <div className="row shipaddr-field">
            <label>Name</label>
            <input
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              type="text"
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && <p>{errors.name.message}</p>}
          </div>

          <div className="row shipaddr-field">
            <label>Address</label>
            <input
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              type="text"
              {...register('address1', { required: 'Address is required' })}
            />
            {errors.address1 && <p>{errors.address1.message}</p>}
          </div>

          <div className="row shipaddr-field">
            <label>Apartment, suite, etc.</label>
            <input
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              type="text"
              {...register('address2')}
            />
          </div>

          <div className="row shipaddr-field">
            <label>City</label>
            <input
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              type="text"
              {...register('city', { required: 'City is required' })}
            />
            {errors.city && <p>{errors.city.message}</p>}
          </div>

          <div className="row shipaddr-field">
            <label>Country & State / Region</label>
            <CountryDropdown
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              name="country"
              value={country}
              valueType="short"
              onChange={(country) => {
                setValue('country', country)
                setCountry(country)
              }}
            />
            <RegionDropdown
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              name="state"
              countryValueType="short"
              valueType="short"
              country={country}
              value={state}
              onChange={(state) => {
                setValue('state', state)
                setState(state)
              }}
            />
            {errors.country && <p>{errors.country.message}</p>}
            {errors.state && <p>{errors.state.message}</p>}
          </div>

          <div className="row shipaddr-field">
            <label>ZIP / Postal Code</label>
            <input
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              type="text"
              {...register('zip', {
                required: 'ZIP is required',
              })}
            />
            {errors.zip && <p>{errors.zip.message}</p>}
          </div>

          <div className="row shipaddr-field">
            <label>Phone</label>
            <input
              disabled={disabled}
              className="col-sm-12 ant-input ant-input-lg"
              type="text"
              {...register('phone', {
                required: 'Please enter a phone number.',
                pattern: {
                  value: /^\+?[\d\s\-./()]{8,20}$/,
                  message: 'Please enter a valid phone number.',
                },
              })}
            />
            {errors.phone && <p>{errors.phone.message}</p>}
          </div>
        </ModalInnerContainer>
        <div className="modal-footer">
          <div className="btns-wrapper actions">
            <Button
              disabled={disabled || loading}
              className="btn-white btn-dash"
              onClick={onHide}
            >
              Cancel Changes
            </Button>
            <Button
              disabled={disabled || loading}
              className="btn-blue btn-dash"
              type="submit"
            >
              Save Address
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default ShippingAddressModal
